/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

import Service from './Service';

class MilestoneService extends Service {
  /**
   * Fetches a single milestone (including its content and additional
   * property) based on the given `milestoneId`.
   *
   * @param {String} milestoneId
   * @returns {Promise}
   */
  fetchById(milestoneId) {
    return this.get(`/internal-api/milestones/${milestoneId}`);
  }

  /**
   * Fetches all versions for a file based on the given `fileId`.
   *
   * @param {String} fileId
   * @returns {Promise}
   */
  fetchByFileId(fileId) {
    return this.get(`/internal-api/files/${fileId}/milestones`);
  }

  /**
   * Returns the name of the first milestone, when available
   *
   * @param {String} fileId
   * @return {Promise<String|null>}
   */
  async getFirstMilestoneNameByFileId(fileId) {
    const milestones = await this.fetchByFileId(fileId);
    if (milestones?.length && milestones[0]?.name) {
      return milestones[0].name;
    }
    return null;
  }

  /**
   * Creates a new milestone.
   *
   * @param {Object} payload
   * @param {String} payload.fileId
   * @param {String} payload.name The milestone's name.
   * @returns {Promise}
   */
  create(payload) {
    return this.post('/internal-api/milestones', payload);
  }

  /**
   * Conditionally creates a new "Autosaved" milestone. The backend
   * determines based on the file content whether this new milestone
   * should be created or not.
   *
   * @param {String} fileId
   * @param {Object} payload
   * @param {String} payload.name The milestone name.
   * @returns
   */
  createAutosaved(fileId, payload) {
    return this.post(`/internal-api/files/${fileId}/milestones/autosave`, payload);
  }

  /**
   * Creates a new diagram off a specific milestone. The milestone's content
   * will be used for the creation.
   *
   * @param {String} milestoneId The milestone's ID that's used as the source.
   * @param {Object} payload
   * @param {String} payload.targetProjectId The target project's ID that will contain the newly created diagram.
   * @param {String} payload.targetFolderId The target folder's ID that will contain the newly created diagram.
   * @param {String} [payload.processId] The process ID (only for DMN and BPMN diagrams).
   * @returns {Promise}
   */
  createDiagramFromMilestone(milestoneId, payload) {
    return this.post(`/internal-api/milestones/${milestoneId}/duplicate`, payload);
  }

  /**
   * Renames a specific milestone based on the given `milestoneId`.
   *
   * @param {String} milestoneId
   * @param {Object} payload
   * @param {String} payload.name The new milestone name.
   * @returns {Promise}
   */
  rename(milestoneId, payload) {
    return this.patch(`/internal-api/milestones/${milestoneId}`, payload);
  }

  /**
   * Publishes a specific milestone to the organization.
   *
   * @param {String} milestoneId
   * @returns {Promise}
   */
  publishToOrganization(milestoneId) {
    return this.patch(`/internal-api/milestones/${milestoneId}`, {
      organizationPublic: true
    });
  }

  unpublishFromOrganization(milestoneId) {
    return this.patch(`/internal-api/milestones/${milestoneId}`, {
      organizationPublic: false
    });
  }

  /**
   * Removes a specific milestone based on the given `milestoneId`.
   *
   * @param {String} milestoneId
   * @returns {Promise}
   */
  destroy(milestoneId) {
    return this.delete(`/internal-api/milestones/${milestoneId}`);
  }

  /**
   * Restores a specific milestone's content into it's associated diagram.
   *
   * @param {String} milestoneId
   * @param {Object} payload
   * @param {String} [payload.processId] The diagram's process ID (only for DMN and BPMN diagrams).
   * @returns {Promise}
   */
  restore(milestoneId, payload) {
    return this.post(`/internal-api/milestones/${milestoneId}/restore`, payload);
  }
}

export default new MilestoneService();
